/**
 * xiaoOS desktop shell — Phase 1 (see CONTEXT.md for the ubiquitous language).
 *
 * Progressive enhancement over the server-rendered pages:
 *  - Desktop (fine pointer, ≥900px): the route's `[data-xw-app]` article is adopted
 *    into a draggable Window on a fixed desktop plane; the dock opens further Apps
 *    by fetching their route and adopting that route's article. URLs stay canonical
 *    via pushState, so every App remains a real, crawlable page.
 *  - Mobile: no windowing — the dock becomes a bottom tab bar and routes navigate
 *    natively (the Companion App presentation).
 *  - No JS / unknown pages: nothing activates; documents render as plain pages.
 */

import { gsap } from 'gsap';
import { ContactFormHandler } from '../components/ContactFormHandler';
import { wallpaperSvg } from './map';
import { getPhone, initPhone } from './phone';
import { decodeTitle } from './decode';
import { emit, emitCabinetPlay, onOs } from './events';
import { initMissions } from './missions';
import { initProfiler } from './profiler';
import { initSound } from './sound';
import type { TerminalHost } from './terminal';

type AppId = 'dossier' | 'projects' | 'resume' | 'contact' | 'arcade';

interface AppSpec {
  id: AppId;
  title: string;
  route: string;
  /** File index and city location shown on the window's label bar. */
  index: string;
  place: string;
  /** Default position as % of the desktop plane. */
  x: number;
  y: number;
}

interface OpenWindow {
  spec: AppSpec;
  el: HTMLElement;
  body: HTMLElement;
  minimized: boolean;
  /** App-specific teardown (projects panel disposer, contact form unbind). */
  dispose?: () => void;
  ready?: Promise<void>;
}

const APPS: AppSpec[] = [
  // Origins (right of the phone) staggered so newly opened windows never bury
  // an earlier titlebar.
  { id: 'dossier', title: 'Dossier', route: '/home', index: '01', place: 'Subject file', x: 1, y: 3 },
  { id: 'projects', title: 'Projects', route: '/projects', index: '02', place: '30 Hudson Yards', x: 3, y: 7 },
  { id: 'resume', title: 'Resume', route: '/resume', index: '03', place: 'One World Trade Center', x: 8, y: 4 },
  { id: 'contact', title: 'Contact', route: '/contact', index: '04', place: 'Brooklyn Bridge', x: 14, y: 10 },
  { id: 'arcade', title: 'Arcade', route: '/arcade', index: '05', place: 'One Times Square', x: 20, y: 6 },
];

/** Pull a just-placed window back inside the desktop when its default
 *  origin would push its titlebar controls past the right edge. */
function keepInside(el: HTMLElement, plane: HTMLElement): void {
  const p = plane.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  const over = r.right - (p.right - 16);
  // Never slide under the docked phone, which sits above the window plane.
  const phone = document.getElementById('xw-dock')?.getBoundingClientRect();
  const floor = phone && phone.width ? phone.right + 16 - p.left : 0;
  if (over > 0) el.style.left = `${Math.max(floor, r.left - p.left - over)}px`;
}

/** Left edge of the window area, clear of the docked phone. */
const beside = (percent: number) => `calc(var(--xw-phone-reserve, 0px) + ${percent}%)`;

const APP_BY_ID = new Map(APPS.map((a) => [a.id, a]));

/** Label-bar tags for the panels that sit outside the apps. */
const PANEL_TAG = { capture: 'Live', file: 'PDF', terminal: '>_' } as const;

/** Set once the 3D City mounts; the shell talks back for room exits etc. */
let cityRef: typeof import('./city3d') | null = null;

function decodeFragment(value: string): string {
  try { return decodeURIComponent(value); }
  catch { return value; } // A malformed bookmark should leave the document usable.
}

function appForPath(pathname: string): AppSpec | null {
  const path = (pathname.replace(/\/+$/, '') || '/').toLowerCase();
  if (path === '/' || path === '/home') return APP_BY_ID.get('dossier') ?? null;
  const found = APPS.find((a) => a.route === path);
  return found ?? null;
}

/** App content that arrives via fetch never got the revealer's ready class on this
 *  document, except elements the initial page load already marked. Strip the marker
 *  classes so nothing mounts invisible inside a window. */
function neutralizeReveals(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>('[data-xw-reveal]').forEach((el) => {
    el.classList.remove('xw-reveal-ready');
    el.classList.add('xw-reveal-shown');
  });
}

export class OSShell {
  private plane: HTMLElement;
  private windows = new Map<AppId, OpenWindow>();
  private zCounter = 10;
  private focused: AppId | null = null;
  private openRequest = 0;
  private readonly loadingStatus: HTMLElement;
  private readonly contentCache = new Map<AppId, HTMLElement>();
  private readonly parked = new Set<AppId>();

  constructor(plane: HTMLElement) {
    this.plane = plane;
    this.loadingStatus = document.createElement('p');
    this.loadingStatus.className = 'xw-app-loading';
    this.loadingStatus.setAttribute('role', 'status');
    this.loadingStatus.hidden = true;
    plane.appendChild(this.loadingStatus);
  }

  /* ────────────────────────────── window lifecycle ───────────────────────── */

  async openApp(id: AppId, opts: { push?: boolean; originRect?: DOMRect | undefined } = {}): Promise<void> {
    const spec = APP_BY_ID.get(id);
    if (!spec) return;
    cityRef?.cancelRoamEntry();

    const request = ++this.openRequest;
    this.loadingStatus.hidden = true;
    const existing = this.windows.get(id);
    if (existing) {
      if (existing.minimized) this.setMinimized(existing, false);
      this.focusWindow(id);
      existing.body.focus({ preventScroll: true });
      emit({ type: 'app', id });
      if (opts.push !== false) this.syncUrl(spec);
      return;
    }

    this.loadingStatus.textContent = `Opening ${spec.title}…`;
    this.loadingStatus.hidden = false;
    const article = await this.resolveContent(spec);
    // Latest navigation wins. A slow response cannot reopen an abandoned app.
    if (request !== this.openRequest) return;
    this.loadingStatus.hidden = true;
    if (!article) {
      // Fetch failed — fall back to a real navigation so the user still lands there.
      window.location.href = spec.route;
      return;
    }

    // Keep the current document available while its replacement is loading.
    for (const otherId of Array.from(this.windows.keys())) {
      if (otherId !== id) this.closeApp(otherId);
    }
    const win = this.createWindow(spec, article);
    this.windows.set(id, win);
    this.plane.appendChild(win.el);
    keepInside(win.el, this.plane);
    this.initAppRuntime(win);
    this.focusWindow(id);
    win.body.focus({ preventScroll: true });
    this.updateDock();
    emit({ type: 'app', id });
    if (opts.originRect) this.growFrom(win, opts.originRect);
    decodeTitle(win.body.querySelector<HTMLElement>('h1'));
    if (opts.push !== false) this.syncUrl(spec);
  }

  /** Window entrance growing out of a map tag/panel (City open motion). */
  private growFrom(win: OpenWindow, origin: DOMRect): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const planeR = this.plane.getBoundingClientRect();
    const final = win.el.getBoundingClientRect();
    win.el.style.animation = 'none'; // suppress the default keyframe entrance
    gsap.fromTo(
      win.el,
      {
        left: origin.left - planeR.left,
        top: origin.top - planeR.top,
        width: origin.width,
        height: origin.height,
        opacity: 0.65,
      },
      {
        left: final.left - planeR.left,
        top: final.top - planeR.top,
        width: final.width,
        height: final.height,
        opacity: 1,
        duration: 0.38,
        ease: 'power3.out',
        onComplete: () => {
          gsap.set(win.el, { clearProps: 'width,height,opacity' });
        },
      },
    );
  }

  /** Adopt the server-rendered article for the current route, or fetch another App's. */
  adoptInitial(article: HTMLElement, id: AppId): void {
    const spec = APP_BY_ID.get(id);
    if (!spec) return;
    const win = this.createWindow(spec, article);
    this.windows.set(id, win);
    this.plane.appendChild(win.el);
    keepInside(win.el, this.plane);
    this.initAppRuntime(win);
    this.focusWindow(id);
    this.updateDock();
    emit({ type: 'app', id });
  }

  private async resolveContent(spec: AppSpec): Promise<HTMLElement | null> {
    const cached = this.contentCache.get(spec.id);
    if (cached) return cached;
    try {
      const res = await fetch(spec.route, { credentials: 'same-origin' });
      if (!res.ok) return null;
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const found = doc.querySelector<HTMLElement>(`[data-xw-app="${spec.id}"]`);
      if (!found) return null;
      const adopted = document.importNode(found, true);
      neutralizeReveals(adopted);
      this.contentCache.set(spec.id, adopted);
      return adopted;
    } catch {
      return null;
    }
  }

  private createWindow(spec: AppSpec, article: HTMLElement): OpenWindow {
    const el = document.createElement('section');
    el.className = 'xw-window';
    el.dataset.app = spec.id;
    el.setAttribute('role', 'region');
    el.setAttribute('aria-label', spec.title);

    const titlebar = document.createElement('header');
    titlebar.className = 'xw-window-titlebar';
    titlebar.innerHTML = `
      <span class="xw-window-tag" aria-hidden="true">${spec.index}</span>
      <span class="xw-window-title">${spec.title}</span>
      <span class="xw-window-place" aria-hidden="true">${spec.place}</span>
      <span class="xw-window-actions">
        <button type="button" class="xw-window-btn" data-xw-win="minimize" aria-label="Minimize ${spec.title}">–</button>
        <button type="button" class="xw-window-btn" data-xw-win="close" aria-label="Close ${spec.title}">×</button>
      </span>`;

    this.contentCache.set(spec.id, article);
    const body = document.createElement('div');
    body.className = 'xw-window-body';
    body.tabIndex = -1;
    body.appendChild(article);

    // Key prompts, as on the game's HUD: how to leave and where to go next.
    const prompts = document.createElement('footer');
    prompts.className = 'xw-window-prompts';
    prompts.innerHTML = `
      <span class="xw-prompt"><kbd class="xw-key">Esc</kbd>Close</span>
      <span class="xw-prompt"><kbd class="xw-key">\`</kbd>Terminal</span>
      <span class="xw-window-prompts-end"><span class="xw-prompt-brand">xiaoOS</span> / ${spec.place}</span>`;

    el.appendChild(titlebar);
    el.appendChild(body);
    el.appendChild(prompts);

    // Cascade slightly per already-open window so stacks never fully overlap.
    const offset = this.windows.size * 3;
    el.style.left = beside(spec.x + offset);
    el.style.top = `${spec.y + offset}%`;

    const win: OpenWindow = { spec, el, body, minimized: false };

    titlebar.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-xw-win]');
      if (!btn) return;
      if (btn.dataset.xwWin === 'close') this.closeApp(spec.id);
      if (btn.dataset.xwWin === 'minimize') this.setMinimized(win, true);
    });
    el.addEventListener('pointerdown', () => this.focusWindow(spec.id), true);
    this.makeDraggable(win.el, titlebar);

    return win;
  }

  private initAppRuntime(win: OpenWindow): void {
    if (win.spec.id === 'projects') {
      win.ready = import('../pages/projectsPanel')
        .then((mod) => {
          if (win.el.isConnected) win.dispose = mod.mountProjectsPage(win.body);
        })
        .catch((error) => console.error('shell: projects panel failed to mount', error));
    }
    if (win.spec.id === 'contact') {
      ContactFormHandler.bind();
      win.dispose = () => ContactFormHandler.unbind();
    }
    if (win.spec.id === 'resume') {
      void import('../pages/resumeTimeline')
        .then((mod) => {
          if (win.el.isConnected) win.dispose = mod.mountResumeLens(win.body);
        })
        .catch((error) => console.error('shell: resume lens failed to mount', error));
    }
    if (win.spec.id === 'dossier') {
      void import('../pages/dossierLive')
        .then((mod) => {
          if (win.el.isConnected) win.dispose = mod.mountDossierLive(win.body);
        })
        .catch((error) => console.error('shell: dossier live failed to mount', error));
    }
  }

  closeApp(id: AppId): void {
    const win = this.windows.get(id);
    if (!win) return;
    const restoreFocus = win.el.contains(document.activeElement);
    win.dispose?.();
    win.el.remove();
    if (restoreFocus) document.querySelector<HTMLElement>(`[data-xw-dock="${id}"]`)?.focus();
    // The article is cached by createWindow; reopening retains document state.
    this.windows.delete(id);
    this.parked.delete(id);
    if (this.focused === id) this.focused = null;
    this.updateDock();
    // Closing a window opened in a room returns to the room; Escape or the
    // room's Leave control flies back out.
  }

  /** Entering a room: open windows step aside (minimise) so the room shows. */
  parkWindows(): void {
    this.windows.forEach((win, id) => {
      if (win.minimized) return;
      this.parked.add(id);
      this.setMinimized(win, true);
    });
  }

  /** Leaving the room restores what was parked and is still around. */
  restoreParked(): void {
    for (const id of this.parked) {
      const win = this.windows.get(id);
      if (win?.minimized) this.setMinimized(win, false);
    }
    this.parked.clear();
  }

  /** Open the Projects app and jump straight to one record (room displays). */
  openProjectRecord(projectId: string): void {
    void this.openApp('projects').then(async () => {
      const win = this.windows.get('projects');
      if (!win) return;
      await win.ready;
      if (!win.el.isConnected) return;
      // Open once, only after listeners exist. Retry-clicking could reopen a
      // record the visitor had already dismissed with Escape.
      const card = Array.from(win.body.querySelectorAll<HTMLElement>('.project-card'))
        .find(card => card.dataset.category === projectId);
      card?.click();
    });
  }

  private setMinimized(win: OpenWindow, minimized: boolean): void {
    win.minimized = minimized;
    win.el.classList.toggle('xw-window--minimized', minimized);
    if (minimized && this.focused === win.spec.id) {
      this.focused = null;
      document.querySelector<HTMLElement>(`[data-xw-dock="${win.spec.id}"]`)?.focus();
    }
    if (!minimized) this.focusWindow(win.spec.id);
    this.updateDock();
  }

  focusWindow(id: AppId): void {
    const win = this.windows.get(id);
    if (!win || win.minimized) return;
    this.zCounter += 1;
    win.el.style.zIndex = String(this.zCounter);
    this.focused = id;
    this.windows.forEach((w) => {
      w.el.classList.toggle('xw-window--focused', w.spec.id === id);
    });
    this.updateDock();
  }

  get focusedApp(): AppId | null {
    return this.focused;
  }

  /* ─────────────────────────── live capture windows ───────────────────────── */

  private captureCount = 0;

  /** Open the Subject's running software in a child window (Live Capture). */
  openCapture(title: string, url: string): void {
    cityRef?.cancelRoamEntry();
    emitCabinetPlay(url);
    this.openFrame('capture', `Live capture — ${title}`, url, `${title} — running live`, 'Close capture');
  }

  /** Open an in-game file (the résumé PDF) in a viewer window. Reopening
   *  focuses the open viewer instead of stacking another. */
  openFile(title: string, url: string, download: string): void {
    cityRef?.cancelRoamEntry();
    if (url === '/resume/pdf') emit({ type: 'file', id: 'resume' });
    const open = this.plane.querySelector<HTMLElement>(`.xw-window--file[data-file="${CSS.escape(url)}"]`);
    if (open) {
      this.zCounter += 1;
      open.style.zIndex = String(this.zCounter);
      open.querySelector<HTMLElement>('[data-xw-win="close"]')?.focus({ preventScroll: true });
      return;
    }
    const el = this.openFrame('file', title, url, title, `Close ${title}`,
      `<a class="xw-window-btn xw-window-btn--text" href="${download}" download>Download</a>`);
    el.dataset.file = url;
    el.querySelector<HTMLElement>('[data-xw-win="close"]')?.focus({ preventScroll: true });
  }

  private openFrame(kind: 'capture' | 'file', title: string, url: string, frameTitle: string, closeLabel: string, actions = ''): HTMLElement {
    const frame = document.createElement('iframe');
    frame.src = url;
    frame.className = 'xw-window-iframe';
    frame.title = frameTitle;
    return this.openPanel(kind, title, frame, closeLabel, actions);
  }

  /** The terminal: one window, reopened in place. */
  openTerminal(host: Omit<TerminalHost, 'close'>): void {
    cityRef?.cancelRoamEntry();
    const open = this.plane.querySelector<HTMLElement>('.xw-window--terminal');
    if (open) {
      this.zCounter += 1;
      open.style.zIndex = String(this.zCounter);
      open.querySelector<HTMLInputElement>('.xw-term-input')?.focus({ preventScroll: true });
      return;
    }
    const root = document.createElement('div');
    const el = this.openPanel('terminal', 'Terminal', root, 'Close terminal');
    let dispose = () => undefined as void;
    const close = () => {
      const restore = el.contains(document.activeElement);
      dispose();
      el.remove();
      if (restore) document.querySelector<HTMLElement>('[data-xw-phone="terminal"]')?.focus();
    };
    el.addEventListener('xw:panel-close', close);
    void import('./terminal').then((m) => {
      if (!el.isConnected) return;
      dispose = m.mountTerminal(root, { ...host, close });
      m.focusTerminal(root);
    });
  }

  private openPanel(kind: 'capture' | 'file' | 'terminal', title: string, content: HTMLElement, closeLabel: string, actions = ''): HTMLElement {
    this.captureCount += 1;
    const el = document.createElement('section');
    // Captures keep the focused styling; they sit outside app focus bookkeeping.
    el.className = `xw-window xw-window--${kind} xw-window--focused`;
    el.dataset.app = kind;
    el.setAttribute('role', 'region');
    el.setAttribute('aria-label', title);

    const titlebar = document.createElement('header');
    titlebar.className = 'xw-window-titlebar';
    titlebar.innerHTML = `
      <span class="xw-window-tag" aria-hidden="true">${PANEL_TAG[kind]}</span>
      <span class="xw-window-title"></span>
      <span class="xw-window-actions">${actions}
        <button type="button" class="xw-window-btn" data-xw-win="close"></button>
      </span>`;
    titlebar.querySelector('.xw-window-title')!.textContent = title;
    const close = titlebar.querySelector<HTMLButtonElement>('[data-xw-win="close"]')!;
    close.textContent = '×';
    close.setAttribute('aria-label', closeLabel);

    const body = document.createElement('div');
    body.className = `xw-window-body xw-window-body--${kind === 'terminal' ? 'terminal' : 'capture'}`;
    body.appendChild(content);

    el.appendChild(titlebar);
    el.appendChild(body);
    el.style.left = beside(6 + ((this.captureCount * 4) % 20));
    el.style.top = `${8 + ((this.captureCount * 5) % 18)}%`;

    // Panels with teardown listen for this; plain frames simply leave.
    titlebar.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('[data-xw-win="close"]')) return;
      if (!el.dispatchEvent(new CustomEvent('xw:panel-close', { cancelable: true }))) return;
      el.remove();
    });
    el.addEventListener('pointerdown', () => {
      this.zCounter += 1;
      el.style.zIndex = String(this.zCounter);
    }, true);
    this.makeDraggable(el, titlebar);

    this.plane.appendChild(el);
    keepInside(el, this.plane);
    this.zCounter += 1;
    el.style.zIndex = String(this.zCounter);
    return el;
  }

  /** Phone Map: step every window aside so the City is the picture. */
  showMap(): void {
    this.openRequest += 1;
    this.loadingStatus.hidden = true;
    this.windows.forEach((win) => {
      if (!win.minimized) this.setMinimized(win, true);
    });
    // Panels (file viewer, captures, terminal) have no phone tile to restore
    // them from, so they close; each reopens from its launcher.
    this.plane.querySelectorAll<HTMLElement>('.xw-window--file, .xw-window--capture, .xw-window--terminal').forEach((el) => {
      if (el.dispatchEvent(new CustomEvent('xw:panel-close', { cancelable: true }))) el.remove();
    });
  }

  /* ──────────────────────────────── dragging ─────────────────────────────── */

  private makeDraggable(el: HTMLElement, handle: HTMLElement): void {
    let startX = 0;
    let startY = 0;
    let baseLeft = 0;
    let baseTop = 0;

    const onMove = (e: PointerEvent) => {
      const planeRect = this.plane.getBoundingClientRect();
      const winRect = el.getBoundingClientRect();
      const maxLeft = Math.max(0, planeRect.width - winRect.width);
      const maxTop = Math.max(0, planeRect.height - Math.min(winRect.height, 120));
      const left = Math.min(Math.max(baseLeft + (e.clientX - startX), 0), maxLeft);
      const top = Math.min(Math.max(baseTop + (e.clientY - startY), 0), maxTop);
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
    };

    handle.addEventListener('pointerdown', (e) => {
      if ((e.target as HTMLElement).closest('[data-xw-win]')) return;
      if (e.button !== 0) return;
      const planeRect = this.plane.getBoundingClientRect();
      const winRect = el.getBoundingClientRect();
      startX = e.clientX;
      startY = e.clientY;
      baseLeft = winRect.left - planeRect.left;
      baseTop = winRect.top - planeRect.top;
      handle.setPointerCapture(e.pointerId);
      handle.addEventListener('pointermove', onMove);
      const stop = () => {
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', stop);
        handle.removeEventListener('pointercancel', stop);
      };
      handle.addEventListener('pointerup', stop);
      handle.addEventListener('pointercancel', stop);
    });
  }

  /* ─────────────────────────────── dock / url ────────────────────────────── */

  private updateDock(): void {
    document.querySelectorAll<HTMLAnchorElement>('#xw-dock [data-xw-dock]').forEach((item) => {
      const id = item.dataset.xwDock as AppId | undefined;
      if (!id) return;
      const win = this.windows.get(id);
      item.classList.toggle('xw-dock-item--open', Boolean(win));
      item.classList.toggle('xw-dock-item--minimized', Boolean(win?.minimized));
      if (this.focused === id) item.setAttribute('aria-current', 'page');
      else item.removeAttribute('aria-current');
    });
  }

  private syncUrl(spec: AppSpec): void {
    if (window.location.pathname !== spec.route) {
      window.history.pushState({ xwApp: spec.id }, '', spec.route);
    }
    const title = document.querySelector('title');
    if (title) document.title = `${spec.title} - David Xiao`;
  }
}

/* ─────────────────────────────── bootstrapping ─────────────────────────────── */

function activateDesktop(initialApp: AppSpec, article: HTMLElement): void {
  document.body.classList.add('xw-os');

  const plane = document.createElement('div');
  plane.className = 'xw-desktop';
  plane.id = 'xw-desktop';
  // SVG wallpaper paints instantly; the 3D City replaces it when it mounts.
  plane.innerHTML = wallpaperSvg();
  document.body.appendChild(plane);

  const shell = new OSShell(plane);
  shell.adoptInitial(article, initialApp.id);
  if (location.hash && !location.hash.startsWith('#record=')) {
    requestAnimationFrame(() => document.getElementById(decodeFragment(location.hash.slice(1)))?.scrollIntoView({ behavior: 'instant' }));
  }

  // Reduced motion retains the static wallpaper and direct application controls.
  // No WebGL download or automatic camera movement is needed for the quiet path.
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!reduced) void import('./city3d')
    .then((m) => {
      const ok = m.mountCity(plane, {
        openApp: (id, rect, hash) => {
          void shell.openApp(id as AppId, { originRect: rect ?? undefined }).then(() => {
            if (hash) document.getElementById(decodeFragment(hash.slice(1)))?.scrollIntoView({ behavior: 'instant', block: 'start' });
          });
        },
        openCapture: (title, url) => shell.openCapture(title, url),
        openProjectRecord: (projectId) => shell.openProjectRecord(projectId),
        enterRoom: () => shell.parkWindows(),
        leaveRoom: () => {
          shell.restoreParked();
          const phone = getPhone();
          if (phone?.root.dataset.roomPocket) {
            delete phone.root.dataset.roomPocket;
            phone.setPocketed(false);
          }
        },
        onFailure: () => { cityRef = null; },
      });
      if (ok) {
        cityRef = m;
        if (window.location.hash === '#roam') m.enterRoam(true);
      }
      // During the apex intro the flight reveals the tags itself.
      if (ok && !document.body.classList.contains('xw-introing')) m.showTags();
    })
    .catch(() => {
      /* keep the SVG wallpaper */
    });

  // Rooms are framed edge to edge: on every arrival in one (tag, landmark,
  // phone tile or terminal), the phone drops to its strip; leaving raises it.
  onOs((e) => {
    if (e.type !== 'room' && e.type !== 'roam') return;
    const phone = getPhone();
    if (phone && phone.root.dataset.state !== 'pocket') {
      phone.root.dataset.roomPocket = '1';
      phone.setPocketed(true);
    }
  });

  // Phone apps open in place.
  const plain = (e: MouseEvent) => !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0);
  document.querySelectorAll<HTMLAnchorElement>('#xw-dock [data-xw-dock]').forEach((item) => {
    item.addEventListener('click', (e) => {
      const id = item.dataset.xwDock as AppId | undefined;
      if (!id || !plain(e)) return;
      e.preventDefault();
      // The window opens now; the camera flies into its room behind it.
      cityRef?.dockDive(id, () => undefined);
      void shell.openApp(id).then(() => {
        // Experience is a section of the Dossier.
        if (item.hash) document.getElementById(decodeFragment(item.hash.slice(1)))?.scrollIntoView({ behavior: 'instant', block: 'start' });
      });
    });
  });
  // Map: back out to the City overview, windows stepped aside.
  document.querySelector<HTMLAnchorElement>('#xw-dock .xw-phone-map')?.addEventListener('click', (e) => {
    if (!plain(e)) return;
    e.preventDefault();
    cityRef?.exitToMap();
    shell.showMap();
  });
  // Terminal: the phone tile or the backquote key, like a game console.
  const terminal = () => shell.openTerminal({
    openApp: (id, hash) => {
      cityRef?.dockDive(id, () => undefined);
      void shell.openApp(id).then(() => {
        if (hash) document.getElementById(decodeFragment(hash.slice(1)))?.scrollIntoView({ behavior: 'instant', block: 'start' });
      });
    },
    openProject: (projectId) => shell.openProjectRecord(projectId),
    openResumePdf: () => shell.openFile('Resume.pdf', '/resume/pdf', '/resume/download'),
    hack: (id) => !!cityRef?.diveIntoRoom(id, (rect) => { void shell.openApp(id, { originRect: rect ?? undefined }); }),
    roam: () => {
      if (!cityRef) return false;
      // The terminal steps aside with the windows; the city is the picture.
      document.querySelectorAll<HTMLElement>('.xw-window--terminal').forEach(el => { if (el.dispatchEvent(new CustomEvent('xw:panel-close', { cancelable: true }))) el.remove(); });
      return cityRef.enterRoam();
    },
    showMap: () => { cityRef?.exitToMap(); shell.showMap(); },
  });
  document.querySelector('#xw-dock [data-xw-phone="terminal"]')?.addEventListener('click', terminal);
  document.addEventListener('keydown', (e) => {
    if (e.key !== '`' || e.metaKey || e.ctrlKey || e.altKey) return;
    if ((e.target as HTMLElement).closest('input, textarea, select, [contenteditable="true"]')) return;
    if (!document.body.classList.contains('xw-boot-done')) return;
    e.preventDefault();
    terminal();
  });

  // Resume.pdf is an in-game file: it opens in a viewer window.
  document.querySelector<HTMLAnchorElement>('#xw-dock [data-xw-file="resume"]')?.addEventListener('click', (e) => {
    if (!plain(e)) return;
    e.preventDefault();
    shell.openFile('Resume.pdf', '/resume/pdf', '/resume/download');
  });

  // In-window links: app routes open windows; hash links scroll within the window.
  plane.addEventListener('click', (e) => {
    const anchor = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href]');
    if (!anchor || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    const href = anchor.getAttribute('href') ?? '';

    // Live capture: run the subject's software in a child window, not a tab.
    if (anchor.classList.contains('xw-observe-live')) {
      e.preventDefault();
      shell.openCapture(anchor.dataset.captureTitle ?? 'Capture', href);
      return;
    }

    if (href.startsWith('#')) {
      const target = document.getElementById(decodeFragment(href.slice(1)));
      if (target) {
        e.preventDefault();
        target.scrollIntoView({ behavior: reduced ? 'instant' : 'smooth', block: 'start' });
      }
      return;
    }

    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    const app = appForPath(url.pathname);
    // Only intercept plain app-route clicks; /resume/pdf etc. navigate normally.
    if (!app || (url.pathname !== app.route && !(app.id === 'dossier' && (url.pathname === '/' || url.pathname === '/home')))) return;
    e.preventDefault();
    if (app.id === 'projects' && url.hash.startsWith('#record=')) {
      shell.openProjectRecord(decodeFragment(url.hash.slice(8)));
      return;
    }
    void shell.openApp(app.id).then(() => {
      if (url.hash) {
        document.getElementById(decodeFragment(url.hash.slice(1)))?.scrollIntoView({ behavior: reduced ? 'instant' : 'smooth', block: 'start' });
      }
    });
  });

  // Escape closes the focused window — unless the projects modal is open (it owns
  // Escape) or the boot/zoom cinematic is still running (Escape means "skip" there).
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (!document.body.classList.contains('xw-boot-done')) return;
    if (document.querySelector('#project-terminal-overlay.is-open')) return;
    const id = shell.focusedApp;
    if (id) shell.closeApp(id);
    else cityRef?.exitRoom(); // no window in focus: Escape leaves the room
  });

  window.addEventListener('hashchange', () => {
    if (window.location.hash === '#roam') cityRef?.enterRoam(true);
    else if (cityRef?.roamActive()) cityRef.exitToMap();
  });

  // Back/forward re-opens the matching app without pushing.
  window.addEventListener('popstate', () => {
    cityRef?.cancelRoamEntry();
    const app = appForPath(window.location.pathname);
    if (app) void shell.openApp(app.id, { push: false });
  });
}

export function initOSShell(): void {
  const page = document.body?.dataset?.page ?? '';
  const initialApp = appForPath(window.location.pathname);
  const article = document.querySelector<HTMLElement>('#main-content [data-xw-app]');
  if (!initialApp || !article || !['home', 'projects', 'resume', 'contact', 'arcade'].includes(page)) return;

  const desktopMode = window.matchMedia('(min-width: 900px) and (pointer: fine)').matches;
  initPhone(!desktopMode);
  initMissions();
  initProfiler();
  initSound();
  if (desktopMode) {
    activateDesktop(initialApp, article);
  } else {
    // Companion App: the dock owns navigation, including Map. The intro
    // arrives in /home#file; a direct /home visit opens the city hub.
    document.body.classList.add('xw-os-mobile');
    // Phones navigate between documents: arriving on one is opening its app.
    if (initialApp.id === 'dossier' && !matchMedia('(prefers-reduced-motion: reduce)').matches) mountMobileMapHome();
    else emit({ type: 'app', id: initialApp.id });
    if (initialApp.id !== 'dossier') decodeTitle(article.querySelector<HTMLElement>('h1'));
    document.querySelector('#xw-dock [data-xw-phone="terminal"]')?.addEventListener('click', openTerminalSheet);
  }
}

/** Mobile terminal: a full-screen sheet; commands navigate like the phone. */
function openTerminalSheet(): void {
  if (document.querySelector('.xw-term-sheet')) return;
  const opener = document.activeElement as HTMLElement | null;
  const sheet = document.createElement('section');
  sheet.className = 'xw-term-sheet';
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-label', 'Terminal');
  sheet.innerHTML = `
    <header class="xw-term-sheet-head">
      <span class="xw-window-tag" aria-hidden="true">&gt;_</span>
      <span class="xw-window-title">Terminal</span>
      <button type="button" class="xw-window-btn" aria-label="Close terminal">×</button>
    </header>`;
  const root = document.createElement('div');
  sheet.appendChild(root);
  document.body.appendChild(sheet);
  document.body.classList.add('xw-mobile-nav-lock');
  // Modal: everything behind the sheet is inert until it closes.
  const behind = [...document.body.children].filter((el): el is HTMLElement => el instanceof HTMLElement && el !== sheet && !el.inert);
  behind.forEach((el) => { el.inert = true; });
  let dispose = () => undefined as void;
  const close = () => {
    dispose();
    sheet.remove();
    behind.forEach((el) => { el.inert = false; });
    document.body.classList.remove('xw-mobile-nav-lock');
    opener?.focus({ preventScroll: true });
  };
  const go = (url: string) => { close(); window.location.href = url; };
  sheet.querySelector('button')!.addEventListener('click', close);
  sheet.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); close(); }
    if (e.key !== 'Tab' || e.defaultPrevented) return;
    const controls = [...sheet.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), a[href], [tabindex="0"]')];
    const index = controls.indexOf(document.activeElement as HTMLElement);
    e.preventDefault();
    controls[(index + (e.shiftKey ? -1 : 1) + controls.length) % controls.length]?.focus();
  });
  void import('./terminal').then((m) => {
    if (!sheet.isConnected) return;
    dispose = m.mountTerminal(root, {
      openApp: (id, hash) => go((APP_BY_ID.get(id)?.route ?? '/home') + (hash ?? (id === 'dossier' ? '#file' : ''))),
      openProject: (projectId) => go(`/projects#record=${encodeURIComponent(projectId)}`),
      openResumePdf: () => {
        emit({ type: 'file', id: 'resume' });
        go('/resume/pdf');
      },
      hack: (id) => {
        // On the map hub the city flies into the room; elsewhere the app opens.
        if (!cityRef || !document.body.classList.contains('xw-map-home')) return false;
        close();
        document.querySelector<HTMLElement>('.xw-dock-item--mob')?.click();
        return cityRef.diveIntoRoom(id, (rect) => { if (!rect) go(APP_BY_ID.get(id)?.route ?? '/home'); });
      },
      roam: () => {
        if (matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
        // The map hub owns free roam (#roam); from elsewhere, go there.
        if (document.body.classList.contains('xw-map-home')) { close(); window.location.hash = 'roam'; }
        else go('/home#roam');
        return true;
      },
      showMap: () => go('/home'),
      close,
    });
    m.focusTerminal(root);
  });
}

/** Fullscreen map hub on mobile /home; the dossier doc toggles via #file. */
function mountMobileMapHome(): void {
  document.body.classList.add('xw-map-home');

  const panel = document.createElement('div');
  panel.className = 'xw-city-mobile';
  panel.setAttribute('aria-label', 'City map — app locations');
  document.body.appendChild(panel);

  const abort = new AbortController();
  const fallback = () => {
    abort.abort();
    panel.remove();
    document.body.classList.remove('xw-map-home', 'xw-map-view');
    document.querySelector('[data-xw-dock="dossier"]')?.setAttribute('aria-current', 'page');
    document.querySelector('.xw-dock-item--mob')?.removeAttribute('aria-current');
    emit({ type: 'app', id: 'dossier' });
  };

  const setView = (map: boolean, sync = true) => {
    document.body.classList.toggle('xw-map-view', map);
    const mapTab = document.querySelector('.xw-dock-item--mob');
    const fileTab = document.querySelector('[data-xw-dock="dossier"]');
    if (map) { mapTab?.setAttribute('aria-current', 'page'); fileTab?.removeAttribute('aria-current'); }
    else { fileTab?.setAttribute('aria-current', 'page'); mapTab?.removeAttribute('aria-current'); }
    if (sync) {
      try {
        const url = new URL(window.location.href);
        url.pathname = '/home';
        url.hash = map ? '' : 'file';
        window.history.replaceState(null, '', url.pathname + url.search + url.hash);
      } catch {
        /* URL cosmetics only */
      }
    }
    if (sync && cityRef?.roamActive()) cityRef.exitToMap();
    if (!map) {
      emit({ type: 'app', id: 'dossier' });
      window.scrollTo(0, 0);
    }
  };

  // #roam is the map in free roam, so the back gesture leaves it.
  const mapHash = () => !window.location.hash || window.location.hash === '#roam';
  let roamPushed = false;
  setView(mapHash(), false);
  window.addEventListener('hashchange', () => {
    setView(mapHash(), false);
    if (window.location.hash === '#roam') {
      roamPushed = true;
      getPhone()?.setPocketed(true);
      cityRef?.enterRoam(true);
    } else if (cityRef?.roamActive()) {
      roamPushed = false;
      cityRef.exitToMap();
    }
    // A room's link into a section of the Dossier (e.g. #experience) lands on it.
    const target = window.location.hash.length > 1 && window.location.hash !== '#file' ? document.getElementById(decodeFragment(window.location.hash.slice(1))) : null;
    target?.scrollIntoView({ behavior: 'instant', block: 'start' });
  }, { signal: abort.signal });
  window.addEventListener('xw:intro-ready', e => {
    if (e instanceof CustomEvent && e.detail?.apex && !window.location.hash) setView(false);
  }, { once: true, signal: abort.signal });
  document.querySelector('.xw-dock-item--mob')?.addEventListener('click', e => {
    if (e instanceof MouseEvent && (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)) return;
    e.preventDefault();
    setView(true);
  }, { signal: abort.signal });

  void import('./city3d')
    .then((m) => {
      const ok = m.mountCity(panel, {
        onFailure: fallback,
        openApp: (id, _rect, hash) => {
          if (id === 'dossier') {
            if (hash) window.location.hash = hash;
            else setView(false);
            return;
          }
          const spec = APP_BY_ID.get(id as AppId);
          if (spec) window.location.href = spec.route + (hash ?? '');
        },
        // No windowing on mobile — in-room surfaces navigate instead.
        openProjectRecord: (projectId) => {
          window.location.href = `/projects#record=${encodeURIComponent(projectId)}`;
        },
        openCapture: (_title, url) => {
          emitCabinetPlay(url);
          window.location.href = url;
        },
        roam: (active) => {
          if (active && window.location.hash !== '#roam') {
            roamPushed = true;
            window.location.hash = 'roam';
          } else if (!active && window.location.hash === '#roam') {
            // Leave the way we came: back if we pushed the entry, else rewrite it.
            if (roamPushed) { roamPushed = false; history.back(); }
            else history.replaceState(null, '', window.location.pathname + window.location.search);
          }
        },
      });
      if (ok) {
        cityRef = m;
        if (!document.body.classList.contains('xw-introing')) m.showTags();
        setView(mapHash(), false);
        if (window.location.hash === '#roam') m.enterRoam(true);
      } else {
        fallback();
      }
    })
    .catch(fallback);
}
